import Alpine from "alpinejs"

declare global {
    const archiveData: {
        apiUrl: string
        totalCount: number
        order: string
        posts: string[]
        page: number
        nextPageUrl: string | null
        pager: string
        filter: Object[]
        filterDetails: Object[] | null
        queryParams: Object
        queryParamsOrder: string[]
        categories: { slug: string, count: number }[] | null
        excludeFromUrl: string[]
        singleDefaults: Record<string, string>
    }
}

const getFilterSection = (key: string) => (Alpine.store('Archive')?.filter ?? []).find(section => section.key === key)

/** Stand-in for a section missing from the payload so its component renders empty instead of throwing. */
const emptySection = (key: string) => ({ key, type: 'term', fields: [], data: { min: 0, max: 0, minValue: 0, maxValue: 0 } })

/** A field whose refreshed count says no offers match is rendered disabled/dimmed. */
const isFieldUnavailable = (field) => field.hasOwnProperty('count') && field.count === 0

const scalar = (value) => Array.isArray(value) ? value[0] : value

const listOf = (value): string[] => value == null || value === ''
    ? [] : (Array.isArray(value) ? value : String(value).split(',')).map(String)

export const Archive = {
    apiUrl: '',
    isLoading: false,
    isLoadingFilter: false,
    queryParams: {} as Record<string, any>,            // Committed: the params the rendered results reflect
    draftQueryParams: {} as Record<string, any>,       // Intended: committed plus every change since, including requests still in flight
    requestSeq: 0,
    queryParamsOrder: [],
    excludeFromUrl: [] as string[],
    singleDefaults: {} as Record<string, string>,  // Single-select filters whose default value is the collection base state
    collectionParams: {} as Record<string, string>,  // Params that must always be sent (e.g., kolekce)
    order: null,
    totalCount: 0,
    posts: [],
    appendPosts: false,
    categories: [] as { slug: string, count: number }[],
    page: 1,
    pager: "",
    filter: null,
    filterUrl: null,
    nextPageUrl: null,
    fetchController: new AbortController(),
    debounceTimer: null,

    setQueryParams(params: Record<string, any> | null = {}, resetPager = false, resetQuery = false, scrollIntoView = false) {
        if (this.isLoading || this.isLoadingFilter) {
            this.fetchController.abort();
            this.fetchController = new AbortController();
        }

        const { noHistory = false, ...changes } = params ?? {}
        const queryParams = params ? {
            ...this.collectionParams,  // Always include collection params for API requests
            ...(resetQuery ? {} : this.draftQueryParams),
            ...changes,
            ...(resetPager ? { [this.queryParamsOrder['strana']]: 1 } : {})
        } : {...this.collectionParams};  // Even when resetting, keep collection params

        document.querySelectorAll('[x-bind="ArchiveQuickLink"]')
            .forEach(el => el.classList.remove('is-active'));

        // The draft is the only source of selection state: controls reflect it right away and
        // responses never write it back (they carry facts only, see applyFilterDetails).
        this.draftQueryParams = queryParams
        this.syncSelection(queryParams)

        // Filter changes refresh counts inline (filtr=1) in the same request; a short debounce
        // coalesces rapid clicks (range slider fires min+max back to back).
        const withFilter = resetPager || resetQuery;
        clearTimeout(this.debounceTimer);
        if (resetPager && !resetQuery) {
            this.debounceTimer = setTimeout(() => this.loadArchive(queryParams, scrollIntoView, true, noHistory), 150);
        } else {
            this.loadArchive(queryParams, scrollIntoView, withFilter, noHistory);
        }
    },

    changeFilterTerm(key, changedField) {
        const section = this.filter.find(section => section.key === key)
        const incompatible = changedField.incompatible ?? []
        this.setQueryParams({[key]: (section?.fields ?? [])
            .filter(term => term.value && !incompatible.includes(term.key))
            .map(term => term.key)}, true);
    },

    changeFilterSelect(key, value) {
        this.setQueryParams({[key]: value}, true);
    },

    changeFilterRadio(key, value) {
        this.setQueryParams({[key]: value}, true);
    },

    removeFilterTerm(key, value) {
        this.setQueryParams({[key]: listOf(this.draftQueryParams[key]).filter(term => term !== String(value))}, true);
    },

    removeFilterTermMulti(keys) {
        const terms = {};
        keys.forEach(key => terms[key] = '');

        this.setQueryParams(terms, true)
    },

    resetFilter() {
        this.setQueryParams(null, true, true)
    },

    changeCategory(category: string) {
        const activeCategory = listOf(this.draftQueryParams[this.queryParamsOrder['kategorie']]);

        !category ? this.resetFilter() : this.setQueryParams({
            [this.queryParamsOrder['kategorie']]: activeCategory.includes(category)
                ? activeCategory.filter((cat: string) => cat !== category) : [...activeCategory, category]
        }, true)
    },

    changeOrder(order = 'DESC', orderBy = '') {
        this.setQueryParams({
            [this.queryParamsOrder['razeni']]: order,
            [this.queryParamsOrder['razeni_podle']]: orderBy
        })
    },

    changePage(page: number) {
        this.setQueryParams({ [this.queryParamsOrder['strana']]: page })
    },

    loadNextPage() {
        this.appendPosts = true
        this.setQueryParams({ [this.queryParamsOrder['strana']]: this.page + 1 })
    },

    /**
     * Canonical API query: params in server-defined order, defaults and empties dropped.
     */
    buildApiQuery(queryParams = {}): URLSearchParams {
        let query = new URLSearchParams();

        if (this.queryParamsOrder && Object.keys(this.queryParamsOrder).length) {
            const orderedQuery = Object.values(this.queryParamsOrder)
                .reduce((object: object, key: string) => {
                    return {...object, [key]: ""}
                }, {}) as { [key: string]: string }

            Object.entries(queryParams).forEach(([key, value]) => {
                const assignedKey = this.queryParamsOrder[key] ?? key
                if (orderedQuery.hasOwnProperty(assignedKey))
                    orderedQuery[assignedKey] = Array.isArray(value) ? value.join(',') : value.toString()
            })

            query = new URLSearchParams(orderedQuery)
        } else {
            Object.entries(queryParams).forEach(([key, value]) => {
                query.append(key, Array.isArray(value) ? value.join(',') : value.toString())
            })
            query.sort()
        }

        const defaultValues = {
            'kategorie': 'all',
            'strana': '1',
            'najeto': '0',
            'rok_od': '1970',
            'rok_do': '2999',
            'zamereni': document.body.dataset.site
        }

        Object.entries(defaultValues).forEach(([key, value]) => {
            if (this.queryParamsOrder.hasOwnProperty(key) && query.get(this.queryParamsOrder[key]) === value)
                query.delete(this.queryParamsOrder[key])
        });

        // Single-select filters at their default value are the collection base state: drop them (the server re-applies the default).
        Object.entries(this.singleDefaults).forEach(([key, value]) => {
            if (query.get(key) === value.toString()) query.delete(key)
        });

        [...query.entries()].forEach(([key, value]) => {
            !value && query.delete(key)
        });

        return query
    },

    async loadArchive(queryParams = {}, scrollIntoView = false, withFilter = false, noHistory = false) {
        const seq = ++this.requestSeq
        this.isLoading = true;
        if (withFilter) this.isLoadingFilter = true;

        const query = this.buildApiQuery(queryParams)

        // Build URL query string excluding collection keys (they're in the URL path)
        const urlQuery = new URLSearchParams(query.toString());
        this.excludeFromUrl.forEach(key => urlQuery.delete(key));
        const urlQueryString = [...urlQuery].length ? `?${decodeURIComponent(urlQuery.toString())}` : '';
        const targetUrl = `${location.pathname}${urlQueryString}`;

        // filtr=1 is API-only (asks the server to include refreshed counts) — never part of the page URL.
        if (withFilter) query.set('filtr', '1');
        const queryString = [...query].length ? `?${decodeURIComponent(query.toString())}` : '';

        await fetch(`${this.apiUrl}${queryString}`, { signal: this.fetchController.signal })
            .then((response) => response.json())
            .then((data) => {
                // A newer request supersedes this one; its response must not commit older state.
                if (seq !== this.requestSeq) return

                if (data.filter) this.applyFilterDetails(data.filter)
                this.posts = !this.appendPosts ? data.posts : [...this.posts, ...data.posts];
                this.categories = data.categories ?? {}
                this.totalCount = data.totalCount
                this.page = data.page
                this.pager = data.pager
                this.nextPageUrl = data.nextPageUrl

                scrollIntoView && !this.appendPosts
                    && document.querySelector('.OffersCatalog-inner')?.scrollIntoView()

                this.filterUrl = targetUrl

                !noHistory && history.pushState({}, '', targetUrl)

                if (data.title && data.description) {
                    document.querySelector('h1').textContent = data.title

                    const metaTitle = document.querySelector('title')
                    metaTitle.textContent = `${data.title} ${metaTitle.textContent.substring(metaTitle.textContent.indexOf("|"))}`

                    document.querySelector('meta[name="description"]')
                        .setAttribute("content", data.description)
                }

                // Default single-select values are the base state — keep them out of the committed params (no active-filter tag).
                const committed = {...queryParams}
                Object.entries(this.singleDefaults).forEach(([key, value]) => {
                    if (scalar(committed[key])?.toString() === value.toString()) delete committed[key]
                })

                this.isLoading = this.isLoadingFilter = this.appendPosts = false
                this.queryParams = committed
            })
            .catch(e => (e.name !== 'AbortError') && location.assign(targetUrl))
    },

    /** Responses carry facts only — counts and range bounds. Selection is re-derived from the draft. */
    applyFilterDetails(details) {
        details.forEach(refreshed => {
            const section = this.filter.find(section => section.key === refreshed.key)
            if (!section) return

            refreshed.fields?.forEach(refreshedField => {
                const field = section.fields?.find(field => field.key === refreshedField.key)
                if (field) field.count = refreshedField.count
            })
            if (refreshed.data && section.data) {
                section.data.min = refreshed.data.min
                section.data.max = refreshed.data.max
            }
        })

        this.syncSelection(this.draftQueryParams)
        this.isLoadingFilter = false
    },

    /** Pushes the intended params into every control: checkboxes, radios, selects and range values. */
    syncSelection(params) {
        this.filter?.forEach(section => {
            if (section.type === 'term') {
                const values = listOf(params[section.key])
                const selected = values.length || !(section.key in this.singleDefaults)
                    ? values : [String(this.singleDefaults[section.key])]
                section.fields?.forEach(field => { field.value = selected.includes(String(field.key)) })
            } else if (section.type === 'select') {
                section.selected = String(scalar(params[section.key]) ?? '') || '0'
            } else if ((section.type === 'range' || section.type === 'range-select') && section.data) {
                const [min, max] = section.type === 'range' ? [section.data.min, section.data.max] : [1970, 2999]
                const minParam = scalar(params[section.nameMin]), maxParam = scalar(params[section.nameMax])
                section.data.minValue = minParam != null && minParam !== '' ? Number(minParam) : min
                section.data.maxValue = maxParam != null && maxParam !== '' ? Number(maxParam) : max
            }
        })
    },

    init() {
        if (!archiveData) return;

        this.apiUrl = archiveData.apiUrl ?? ''
        this.queryParams = archiveData.queryParams ?? {}
        this.draftQueryParams = {...this.queryParams}
        this.queryParamsOrder = archiveData.queryParamsOrder ?? []
        this.excludeFromUrl = archiveData.excludeFromUrl ?? []
        this.singleDefaults = archiveData.singleDefaults ?? {}

        // Extract collection params that must always be sent with API requests
        this.collectionParams = {}
        this.excludeFromUrl.forEach(key => {
            if (this.queryParams[key]) {
                this.collectionParams[key] = this.queryParams[key]
            }
        })

        this.order = archiveData.order ?? {}
        this.totalCount = archiveData.totalCount ?? 0
        this.posts = archiveData.posts ?? []
        this.categories = archiveData.categories ?? []
        this.page = archiveData.page ?? 1
        this.pager = archiveData.pager ?? ""
        this.filter = archiveData.filter ?? null
        this.nextPageUrl = archiveData.nextPageUrl ?? null

        this.filterUrl = Alpine.$persist('').as('offersFilterUrl').using(sessionStorage)
        if (this.filter) {
            archiveData.filterDetails && this.applyFilterDetails(archiveData.filterDetails)
            this.syncSelection(this.queryParams)
        }

        window.addEventListener("popstate", (e) => {
            const target = e.target as Window
            if (!target) return

            const queryParams = {};
            const searchParams = new URLSearchParams(target.location.search)

            searchParams.forEach((value, key) => {
                queryParams[key] = value.split(",")
            })

            this.setQueryParams({...queryParams, noHistory: true }, false, true)
        })
    }
}

export const ArchiveFilterTerms = (key: string) => {
    const paramsOrder = Alpine.store('Archive')?.queryParamsOrder ?? {}
    const alwaysExpanded = key === (paramsOrder['rada'] ?? 'rada') || key === (paramsOrder['model'] ?? 'model')
    const collapseAfter = key === (paramsOrder['stav'] ?? 'stav') ? 2 : 6
    const collapseWhileOver = key === (paramsOrder['stav'] ?? 'stav') ? 2 : 8

    return {
        section: getFilterSection(key) ?? emptySection(key),
        allVisible: alwaysExpanded,
        get visibleCount() {
            return (this.section.fields?.length ?? 0) > collapseWhileOver ? collapseAfter : collapseWhileOver
        },
        get shouldCollapse() {
            if (alwaysExpanded || this.section.hasOwnProperty('render')) return false

            const fields = this.section.fields ?? []
            return fields.length > collapseWhileOver && fields.some((field, index) => !field.value && index >= collapseAfter)
        },
        term: {
            [':name']() { return `${this.section.key}[]` },
            [':value']() { return this.field.key },
            [':disabled']() { return isFieldUnavailable(this.field) },
            ['@change']() {
                this.$nextTick(() => { Alpine.store('Archive').changeFilterTerm(this.section.key, this.field) })
            }
        },
        termRadio: {
            [':name']() { return this.section.key },
            [':value']() { return this.field.key },
            [':checked']() { return !!this.field.value },
            [':disabled']() { return isFieldUnavailable(this.field) },
            ['@change']() {
                Alpine.store('Archive').changeFilterRadio(this.section.key, this.field.key)
            }
        },
        termCheck: {
            [':class']() { return { 'is-disabled': isFieldUnavailable(this.field) } },
            ['x-show']() { return this.allVisible || this.fieldIndex < this.visibleCount || !!this.field.value }
        },
        toggleTermCollapse: {
            [':class']() { return { 'is-opened': this.allVisible }},
            ['@click']() { this.allVisible = !this.allVisible }
        },
    }
}

export const ArchiveFilterSelect = (key: string) => ({
    section: getFilterSection(key) ?? emptySection(key),
    select: {
        [':name']() { return this.section.key },
        ['@change']() {
            Alpine.store('Archive').changeFilterSelect(this.section.key, this.$el.value)
        }
    }
})

export const ArchiveFilterRange = (key: string, step: number) => ({
    section: getFilterSection(key) ?? emptySection(key),
    step: step,
    min: 0,
    max: 0,
    interacting: false,

    get minimum() { return this.section.data.min },
    get maximum() { return this.section.data.max },
    get span() { return this.maximum - this.minimum },
    // Nothing to slide within a single step: both handles would pin to one end (an input with min == max).
    get frozen() { return this.span <= this.step },

    clamp(value: number) {
        return Math.min(Math.max(value, this.minimum), this.maximum)
    },

    init() {
        // Handles follow the section data (bounds are server facts, values the client draft).
        // Mid-drag nothing may move them — the user is about to replace the values anyway.
        Alpine.effect(() => {
            const { minValue, maxValue } = this.section.data
            if (this.interacting) return

            this.min = this.clamp(minValue)
            this.max = this.clamp(maxValue)
            if (this.min > this.max) [this.min, this.max] = [this.max, this.min]
        })
    },

    setRangeValues() {
        if (!Number.isFinite(this.min) || !Number.isFinite(this.max)) return

        this.interacting = false
        Alpine.store('Archive').setQueryParams({
            [this.section.nameMin]: this.clamp(this.min),
            [this.section.nameMax]: this.clamp(this.max)
        }, true)
    },

    adjustOtherSliderValue(slider, newValue) {
        if (!Number.isFinite(newValue)) return

        if (slider === 'max') {
            if (newValue < this.max) return
            this.max = Math.min(newValue + this.step, this.maximum)
        } else {
            if (newValue > this.min) return
            this.min = Math.max(newValue - this.step, this.minimum)
        }
    },

    rangeSlider: {
        [':class']() { return { 'is-disabled': this.frozen, 'is-single': this.span <= 0 } },
        ['@click'](e) {
            if (e.target !== this.$el || this.frozen)
                return

            const centerPosition = (this.min - this.minimum) / this.span + (this.max - this.min) / this.span * .5
            const getClickedPosition = (offset = 0) => (e.offsetX + offset) / e.target.offsetWidth

            if (getClickedPosition() >= centerPosition) {
                this.max = this.clamp(Math.round(getClickedPosition(-8) * this.span / this.step) * this.step + this.minimum)
                this.adjustOtherSliderValue('min', this.max)
            } else {
                this.min = this.clamp(Math.round(getClickedPosition(8) * this.span / this.step) * this.step + this.minimum)
                this.adjustOtherSliderValue('max', this.min)
            }

            this.setRangeValues()
        }
    },
    rangeMin: {
        [':name']() { return this.section.nameMin ?? 'min' },
        [':step']() { return this.step },
        [':min']() { return this.minimum },
        [':max']() { return Math.max(this.maximum - this.step, this.minimum) },
        [':disabled']() { return this.frozen },
        ['@change']() { this.setRangeValues() },
        ['@input']() {
            this.interacting = true
            this.adjustOtherSliderValue('max', this.$el.valueAsNumber)
        }
    },
    rangeMax: {
        [':name']() { return this.section.nameMax ?? 'max' },
        [':step']() { return this.step },
        [':min']() { return Math.min(this.minimum + this.step, this.maximum) },
        [':max']() { return this.maximum },
        [':disabled']() { return this.frozen },
        ['@change']() { this.setRangeValues() },
        ['@input']() {
            this.interacting = true
            this.adjustOtherSliderValue('min', this.$el.valueAsNumber)
        }
    },
    rangeIndicator: {
        [':style']() {
            // Frozen slider shows just the dimmed empty track — a full dark bar reads as a selection.
            if (this.frozen) return { width: '0%', left: '0%' }

            return {
                width: Math.min(Math.abs((this.min - this.max) / this.span * 100), 100) + '%',
                left: Math.abs((this.min - this.minimum) / this.span * 100) + '%'
            }
        }
    }
})

export const ArchiveFilterRangeSelect = (key: string) => ({
    section: getFilterSection(key) ?? emptySection(key),
    min: 1970,
    max: 2999,

    get minAvailableOptions() {
        return (this.section.options ?? []).filter(option => option.key <= this.max)
    },
    get maxAvailableOptions() {
        return (this.section.options ?? []).filter(option => option.key >= this.min)
    },

    init() {
        Alpine.effect(() => {
            this.min = this.section.data.minValue
            this.max = this.section.data.maxValue
        })
    },

    inputMin: {
        [':class']() { return { 'is-empty': this.min === 1970 } },
    },
    selectMin: {
        [':name']() { return this.section.nameMin ?? 'min' },
        ['@change']() {
            this.$nextTick(() => Alpine.store('Archive').setQueryParams({
                [this.section.nameMin]: this.$el.value
            }, true))
        }
    },
    optionMin: {
        [':value']() { return this.option.key },
        [':selected']() { return this.min === this.option.key },
        [':disabled']() { return !this.minAvailableOptions.find(option => option.key === this.option.key) },
        [':style']() { return !this.minAvailableOptions.find(option => option.key === this.option.key) ? { display: 'none' } : {} },
        ['x-text']() { return `${this.option.label}` }
    },
    inputMax: {
        [':class']() { return { 'is-empty': this.max === 2999 } },
    },
    selectMax: {
        [':name']() { return this.section.nameMax ?? 'max' },
        ['@change']() {
            this.$nextTick(() => Alpine.store('Archive').setQueryParams({
                [this.section.nameMax]: this.$el.value
            }, true))
        }
    },
    optionMax: {
        [':value']() { return this.option.key },
        [':selected']() { return this.max === this.option.key },
        [':disabled']() { return !this.maxAvailableOptions.find(option => option.key === this.option.key) },
        [':style']() { return !this.maxAvailableOptions.find(option => option.key === this.option.key) ? { display: 'none' } : {} },
        ['x-text']() { return `${this.option.label}` }
    }
})

export const ArchiveFilterSelection = () => ({
    get selection() {
        const selected = []
        const pushSelectedTag = (key, value) => {
            const section = Alpine.store('Archive').filter.find(section => key.includes(section.key))
            if (value === '') return;

            if (section?.type === 'term' || section?.type === 'select') {
                const term = section?.[section.type === 'select' ? 'options' : 'fields']?.find(term => term.key.toString() === value.toString())
                term && selected.push({ title: section?.title, label: term.label, value: value, key: key })
            } else if (section?.type === 'range') {
                const includedInQuery = Alpine.store('Archive').queryParams.hasOwnProperty(section.nameMin)
                    || Alpine.store('Archive').queryParams.hasOwnProperty(section.nameMax)

                includedInQuery && !selected.find(el => el.title === section.title) && selected.push({
                    title: section?.title,
                    label: `${parseInt(section.data.minValue).toLocaleString()} Kč - ${parseInt(section.data.maxValue).toLocaleString()} Kč`,
                    key: [section.nameMin, section.nameMax]
                })
            } else if (section?.type === 'range-select') {
                const includedInQuery = (Alpine.store('Archive').queryParams.hasOwnProperty(section.nameMin) && parseInt(Alpine.store('Archive').queryParams[section.nameMin]) !== 1970)
                    || (Alpine.store('Archive').queryParams.hasOwnProperty(section.nameMax) && parseInt(Alpine.store('Archive').queryParams[section.nameMax]) !== 2999)

                includedInQuery && !selected.find(el => el.title === section.title) && selected.push({
                    title: section?.title,
                    label: `${Math.max(parseInt(section.data.minValue), section.data.min)} - ${Math.min(parseInt(section.data.maxValue), section.data.max)}`,
                    key: [section.nameMin, section.nameMax]
                })
            } else if (key === (Alpine.store('Archive')?.queryParamsOrder['hledat'] ?? 'hledat')) {
                selected.push({ title: 'Hledaný výraz', label: value, value: value, key: key })
            }
        }

        Object.entries(Alpine.store('Archive').queryParams).forEach(([key, value]) => {
            const queryValue = typeof value === 'string' && value.includes(',') ? value.split(',') : value
            if (!['strana', 'razeni', 'razeni_podle'].includes(key))
                Array.isArray(queryValue) ? queryValue.forEach(term => pushSelectedTag(key, term))
                    : pushSelectedTag(key, queryValue)
        })

        return selected
    },
    title: {
        ['x-text']() { return this.term.title }
    },
    valueLabel: {
        ['x-text']() { return this.term.label }
    },
    removeTerm: {
        ['@click.prevent']() {
            !Array.isArray(this.term.key) ? Alpine.store('Archive').removeFilterTerm(this.term.key, this.term.value)
                : Alpine.store('Archive').removeFilterTermMulti(this.term.key)
        }
    },
    reset: {
        ['@click.prevent']() { Alpine.store('Archive').resetFilter() }
    }
})

export const ArchiveCategoryNav = () => ({
    tag: '',
    init() {
        this.tag = this.$el.querySelector('select[name="field"] option[selected]')?.value ?? ''
    },
    get counts() {
        return Alpine.store('Archive').categories.reduce((counts, category) => {
            return {...counts, ...{[category.slug]: category.count}}
        }, {})
    },
    ArchiveCategoryLink: {
        [':class']() {
            const targetCategory = this.$el.dataset.categorySlug,
                activeCategory = Alpine.store('Archive').queryParams.kategorie

            const categoryObject = Alpine.store('Archive').categories
                .find(category => category.slug === (targetCategory ?? ''))

            return {
                'is-empty': categoryObject && categoryObject.count < 1,
                'is-active': (activeCategory && activeCategory.includes(targetCategory))
                    || (!targetCategory && (activeCategory === 'all' || !activeCategory) )
            }
        },
        ['@click.prevent']() {
            const categorySlug = this.$el.dataset.categorySlug
            if (!categorySlug) this.tag = ''
            Alpine.store('Archive').changeCategory(categorySlug);
        }
    },
    ArchiveCategoryCount: {
        ['x-text']() { return this.counts ? this.counts[this.$el.parentElement?.dataset.categorySlug ?? ''] : '' }
    }
})

export const ArchiveFilterOpener = {
    '@click'() {
        this.filterOpened = !this.filterOpened
        this.filterOpened && document.dispatchEvent(new CustomEvent("updateFilterSticky"))
    }
}

/** Mobile filter modal: opening must also force the <details> open — its summary is hidden below md. */
export const ArchiveFilterModalOpen = {
    '@click'() {
        const details = document.querySelector('details.CatalogFilter-inner') as HTMLDetailsElement | null
        if (details) details.open = true
        this.filterModal = true
        document.body.classList.add('u-hiddenOverflow')
        document.dispatchEvent(new CustomEvent("updateFilterSticky"))
    }
}

export const ArchiveFilterModalClose = {
    '@click'() {
        this.filterModal = false
        document.body.classList.remove('u-hiddenOverflow')
    }
}

export const ArchiveFilterModalBackdrop = {
    '@click.self'() {
        this.filterModal = false
        document.body.classList.remove('u-hiddenOverflow')
    },
    '@keydown.escape.window'() {
        if (this.filterModal) {
            this.filterModal = false
            document.body.classList.remove('u-hiddenOverflow')
        }
    }
}

export const ArchiveGrid = {
    ':class'() { return Alpine.store('Archive').isLoading && 'is-loading' },
    'x-html'() { return Alpine.store('Archive').posts.join('\n') }
}

export const ArchiveNoResults = {
    'x-show'() { return !Alpine.store('Archive').posts?.length }
}

export const ArchiveTotalCount = {
    'x-text'() { return Alpine.store('Archive').totalCount ?? 0 }
}

/** Czech plural (1 / 2–4 / 5+) of the count label, picked from the server-translated forms in data-plural. */
export const ArchiveTotalCountLabel = {
    'x-text'() {
        const [one, few, many] = JSON.parse(this.$el.dataset.plural)
        const count = Alpine.store('Archive').totalCount ?? 0

        return count === 1 ? one : count >= 2 && count <= 4 ? few : many
    }
}

export const ArchiveResetFilter = {
    '@click.prevent'() { Alpine.store('Archive').resetFilter() }
}

export const ArchiveTagSelect = {
    '@change.prevent'() {
        this.$nextTick(() => { Alpine.store('Archive').setQueryParams({'zamereni': this.$el.value}, true) })
    }
}

export const ArchiveQuickLink = {
    '@click.prevent'() {
        const url = new URL(this.$el.href ?? this.$el.dataset.url),
            searchParams = url?.searchParams ?? null

        searchParams && Alpine.store('Archive')
            .setQueryParams(Object.fromEntries(searchParams.entries()), true, true, true)

        this.$el.classList.add('is-active')
    }
}

export const ArchiveStickyBar = {
    '@scroll.window.passive'() {
        const rect = this.$el.getBoundingClientRect()
        // Phones reveal the fixed bar only once the whole in-flow block scrolled away;
        // desktop keeps the original threshold (its bar is the block itself, position: sticky).
        const sticked = window.innerWidth < 768 ? rect.bottom <= 80 : rect.top <= 80
        this.$el.classList.toggle('is-sticked', sticked)
    }
}

export const ArchiveOrder = {
    // Two instances exist on mobile (in-flow row + fixed bar) — bind value so they stay in sync.
    ':value'() { return Alpine.store('Archive').order },
    '@change.prevent'() {
        const order = this.$el.value
        !order.includes('|') ? Alpine.store('Archive').changeOrder(order)
            : Alpine.store('Archive').changeOrder(order.split('|')[1], order.split('|')[0]);
    }
}

export const ArchivePager = {
    ':class'() { return Alpine.store('Archive').isLoading && 'is-loading' },
    'x-html'() { return Alpine.store('Archive').pager }
}

export const ArchivePagerLink = {
    '@click.prevent'() {
        const pageUrl = new URLSearchParams((new URL(this.$el.getAttribute('href'))).search);
        Alpine.store('Archive').changePage(Number(pageUrl.get(Alpine.store('Archive').queryParamsOrder['strana'] ?? 'strana') ?? 1));
    }
}

export const ArchiveLoadMore = {
    'x-show'() { return Alpine.store('Archive').nextPageUrl ?? false },
    ':href'() { return Alpine.store('Archive').nextPageUrl },
    ':class'() { return Alpine.store('Archive').isLoading && 'is-loading' },
    '@click.prevent'() {
        Alpine.store('Archive').loadNextPage()
    }
}